<template>
  <div ref="refInfoSlot" class="info-slot">
    <div v-if="showStickySearchBar" class="sticky-top-input">
      <!-- clone search units to run locally on slot -->
      <div v-if="computedShowFilterUnits" class="unit-filter-header">
        <faceted-search
          :key="unitSearchKey"
          :options="unitSearchOptions"
          :default-field="unitSearchDefaultField"
          class="unit-filter-header__search"
          @search="openFirstUnitMatch"
        />

        <div class="unit-filter-header__actions">
          <b-button
            size="sm"
            class="px-50"
            :variant="localMatchesSearch ? 'primary' : 'outline-primary'"
            :pressed="localMatchesSearch"
            v-b-tooltip.hover
            :title="$t('unit.toggle_detail_search')"
            @click="localMatchesSearch = !localMatchesSearch"
          >
            <feather-icon icon="TypeIcon" size="14" />
          </b-button>

          <b-button variant="link" size="sm" class="p-0" @click="handleCloseUnit">
            <feather-icon icon="XIcon" size="24" />
          </b-button>
        </div>
      </div>

      <b-input-group v-if="matchesSearchVisible">
        <b-form-input
          type="text"
          v-model="searchIntoSlotContainerQuery"
          :placeholder="$t('grid.widgets.units.search.placheholderMatches')"
          @keyup.enter="nextMatch()"
          @input="checkMatches()"
        />
        <b-input-group-append v-if="matchesExists">
          <b-button
            variant="primary"
            class="px-1"
            :disabled="disabledPrevMatchButton"
            @click="prevMatch()"
          >
            <feather-icon icon="ChevronUpIcon" size="16" />
          </b-button>
          <b-input-group-text> {{ currentIndex }}/{{ matchCount }} </b-input-group-text>
          <b-button
            variant="primary"
            class="px-1"
            :disabled="disabledNextMatchButton"
            @click="nextMatch()"
          >
            <feather-icon icon="ChevronDownIcon" size="16" />
          </b-button>
        </b-input-group-append>
      </b-input-group>
    </div>
    <div class="info-slot-container">
      <!-- Botón exportar -->
      <div class="position-relative pt-0">
        <b-button
          size="sm"
          @click="exportToPDF()"
          variant="outline-info"
          class="text-capitalize alarm-export-button"
          v-if="!source === 'camera' && !source === 'subsystem_tree'"
          type="button"
        >
          <feather-icon icon="DownloadCloudIcon" size="14" />
        </b-button>
      </div>
      <!-- ===== Encabezado de Alarma ===== -->
      <div v-if="isAlarmSource" class="info-slot-alarm w-100">
        <header class="text-center report-header">
          <h1 class="h3 fw-bold text-dark mb-0">
            <strong>{{ data.data.alarm_type.description }}</strong>
          </h1>
          <h1 class="h4 fw-bold text-dark">
            <strong>{{ data.data.unit.description }}</strong>
          </h1>
          <p class="text-header">
            {{ $t("grid.slots.info.alarm_id") }}:
            <span
              class="text-id-alarm d-inline-flex align-items-center fw-bold clickable"
              @click="copyAlarmId"
              v-b-tooltip.hover.top="copiedTooltip"
            >
              {{ data.data.id }}
              <feather-icon icon="CopyIcon" size="14" class="ml-1 copy-icon" />
            </span>
          </p>

          <p class="text-header mb-0">
            {{ $t("grid.slots.info.creation_date") }}:
            {{ dateParser.parseDateTimezone(data.data.created_format_2) }}
          </p>
        </header>
      </div>
      <!-- ===== Resumen General (FUERA DE LAS TABS) ===== -->
      <section-collapse
        v-if="isAlarmSource"
        :title="$t('grid.slots.info.general_summary')"
        icon="ClipboardIcon"
        :unique-id="uniqueId"
        section-key="Resumen"
        :default-open="true"
        class="mt-1"
      >
        <div class="table-responsive">
          <table class="table table-borderless mb-0">
            <tr>
              <th>{{ $t("alarm.status") }}</th>
              <td class="fw-bold">{{ alarmStatus }}</td>
            </tr>
            <tr>
              <th>Tiempo en estado</th>
              <td class="fw-bold">{{ elapsedFormatted }}</td>
            </tr>
            <tr>
              <th>{{ $t("grid.slots.info.unit") }}</th>
              <td class="fw-bold" v-html="data.data.unit.description"></td>
            </tr>
            <tr v-if="data.data.tenant">
              <th>{{ $tc("tenants.title", 1) }}</th>
              <td class="fw-bold">
                <div>{{ data.data.tenant.name }}</div>
                <div v-html="data.data.tenant.description"></div>
              </td>
            </tr>
            <tr v-if="data.data.unit.address">
              <th>{{ $t("grid.slots.info.address") }}</th>
              <td>
                {{ data.data.unit.address }}
                {{ data.data.unit.exterior_number }},
                {{ data.data.unit.municipality.name }},
                {{ data.data.unit.suburb }}
              </td>
            </tr>
            <tr>
              <th>{{ $t("grid.widgets.alarms.menu.reports") || "Reportes" }}</th>
              <td>
                <div class="d-flex flex-wrap gap-2">
                  <b-button
                    v-for="tpl in alarmFormTemplates"
                    :key="'tpl-' + tpl.id"
                    size="sm"
                    variant="info"
                    @click.prevent="navigateToFormTemplate(tpl)"
                  >
                    {{ tpl.name }}
                  </b-button>
                </div>
              </td>
            </tr>
            <tr
              v-if="
                ['waiting', 'attending', 'canceled', 'attended'].includes(
                  data.data.alarm_status
                )
              "
            >
              <th>{{ $t("grid.slots.info.actions") }}</th>
              <td>
                <div class="d-flex flex-wrap gap-2">
                  <b-button
                    size="sm"
                    variant="success"
                    @click="
                      takeCall(
                        userData,
                        data.data.sip_call[0].sessionId,
                        data.data.sip_call[0].callingPartyNumber,
                        data
                      )
                    "
                    v-if="
                      data.data.sip_call[0]
                        ? data.data.sip_call[0].state === 'waiting'
                        : false
                    "
                  >
                    {{ $t("grid.slots.info.answer_call") }}
                  </b-button>

                  <b-button
                    size="sm"
                    variant="warning"
                    @click.prevent="onClickUpdateAlarmStatus('send_to_review')"
                    v-if="
                      ['attending'].includes(data.data.alarm_status) &&
                      $can('validate_alarms', 'Alarm') &&
                      $can('maker_checker_flow', 'Alarm')
                    "
                  >
                    Enviar a revisión
                  </b-button>

                  <b-button
                    size="sm"
                    variant="success"
                    @click.prevent="onClickUpdateAlarmStatus('waiting')"
                    v-if="
                      ['waiting'].includes(data.data.alarm_status) &&
                      $can('update_attention', 'Alarm')
                    "
                  >
                    {{ $t("grid.slots.info.btns.confirmed") }}
                  </b-button>

                  <b-button
                    size="sm"
                    variant="success"
                    @click.prevent="onClickUpdateAlarmStatus('attending')"
                    v-if="
                      ['attending'].includes(data.data.alarm_status) &&
                      $can('update_attend', 'Alarm')
                    "
                  >
                    {{ $t("grid.slots.info.btns.attended") }}
                  </b-button>

                  <b-button
                    size="sm"
                    variant="danger"
                    @click.prevent="onClickUpdateAlarmStatus('canceled')"
                    v-if="
                      ['waiting', 'attending'].includes(data.data.alarm_status) &&
                      $can('update_cancel', 'Alarm')
                    "
                  >
                    {{ $t("grid.slots.info.btns.cancel") }}
                  </b-button>

                  <b-button
                    size="sm"
                    variant="primary"
                    @click.prevent="onClickReopenAlarm"
                    v-if="
                      ['canceled', 'attended'].includes(data.data.alarm_status) &&
                      $can('reopen_alarm', 'Alarm')
                    "
                  >
                    {{ $t("grid.slots.info.btns.reopen") }}
                  </b-button>
                </div>
              </td>
            </tr>
          </table>
        </div>
      </section-collapse>

      <!-- ===== Detalles de la Unidad ===== -->
      <section-collapse
        v-if="isAlarmSource"
        :title="$t('grid.slots.info.facility_detail')"
        icon="MapPinIcon"
        :unique-id="uniqueId"
        section-key="Detalles"
        :default-open="true"
      >
        <div class="table-responsive">
          <table class="table table-borderless mb-0">
            <tr>
              <th>{{ $t("grid.slots.info.description") }}</th>
              <td class="fw-bold">{{ data.data.unit.description }}</td>
            </tr>
            <tr>
              <th>{{ $t("grid.slots.info.company") }}</th>
              <td class="fw-bold">{{ data.data.unit.company }}</td>
            </tr>
            <tr>
              <th>{{ $t("grid.slots.info.latitude") }}</th>
              <td class="fw-bold">{{ data.data.unit_coordinates.lat }}</td>
            </tr>
            <tr>
              <th>{{ $t("grid.slots.info.longitude") }}</th>
              <td class="fw-bold">{{ data.data.unit_coordinates.lng }}</td>
            </tr>
            <tr>
              <th>{{ $t("grid.slots.info.address") }} 1</th>
              <td class="fw-bold">{{ data.data.unit.address_1 }}</td>
            </tr>
            <tr>
              <th>{{ $t("grid.slots.info.address") }} 2</th>
              <td class="fw-bold">{{ data.data.unit.address_2 }}</td>
            </tr>
            <tr>
              <th>{{ $t("grid.slots.info.exterior_number") }}</th>
              <td class="fw-bold">{{ data.data.unit.exterior_number }}</td>
            </tr>
            <tr>
              <th>{{ $t("grid.slots.info.suburb") }}</th>
              <td class="fw-bold">{{ data.data.unit.suburb }}</td>
            </tr>
            <tr>
              <th>{{ $t("grid.slots.info.municipality") }}</th>
              <td class="fw-bold">
                {{ data.data.unit.municipality ? data.data.unit.municipality.name : "" }}
              </td>
            </tr>
            <tr>
              <th>{{ $t("grid.slots.info.postal_code") }}</th>
              <td class="fw-bold">{{ data.data.unit.postal_code }}</td>
            </tr>
            <tr>
              <th>{{ $t("grid.slots.info.country") }}</th>
              <td class="fw-bold">
                {{ data.data.unit.country ? data.data.unit.country.name : "" }}
              </td>
            </tr>
            <tr>
              <th>{{ $t("grid.slots.info.region") }}</th>
              <td class="fw-bold">
                {{ data.data.unit.region ? data.data.unit.region.name : "" }}
              </td>
            </tr>
            <tr>
              <th>{{ $t("grid.slots.info.region") }}</th>
              <td>{{ unit.country ? unit.region.name : " - " }}</td>
            </tr>
            <tr>
              <th>REMS</th>
              <td>{{ data.data.unit.rems }}</td>
            </tr>
            <tr>
              <th>SIRH</th>
              <td>{{ data.data.unit.sirh }}</td>
            </tr>
            <tr>
              <th>Id SEPROBAN</th>
              <td>{{ data.data.unit.id_seproban }}</td>
            </tr>
          </table>
        </div>
      </section-collapse>

      <!-- Camera -->
      <section-collapse
        v-if="source === 'camera'"
        :title="$t('grid.slots.info.camera_title')"
        icon="CameraIcon"
        :unique-id="uniqueId"
        section-key="Camera"
        :default-open="true"
        capturable
        :hide-chevron="source === 'camera'"
      >
        <camera-data :data="data.data.camera" />
      </section-collapse>

      <!-- Unit -->
      <section-collapse
        v-if="[SOURCES.UNIT, SOURCES.SUBSYSTEM_TREE].includes(source)"
        :title="$t('grid.slots.info.unit')"
        icon="InfoIcon"
        :unique-id="uniqueId"
        section-key="Unit"
        :default-open="source === SOURCES.SUBSYSTEM_TREE"
        :hide-chevron="source === SOURCES.SUBSYSTEM_TREE"
        capturable
        ref="refUnitDataCollapse"
      >
        <unit-data :key="data.data.id" :data="data.data" @set-unit="setUnit" />
      </section-collapse>

      <!-- Información Adicional -->
      <section-collapse
        v-if="
          [SOURCES.UNIT, SOURCES.SUBSYSTEM_TREE].includes(source) &&
          unit.additional_info &&
          unit.additional_info.length
        "
        :title="$t('unit.additional_info')"
        icon="InfoIcon"
        :unique-id="uniqueId"
        section-key="AdditionalInfo"
        :default-open="false"
        capturable
        ref="refAdditionalInfoCollapse"
      >
        <additional-information :additional-information="unit.additional_info" />
      </section-collapse>

      <!-- Contactos de emergencia -->
      <section-collapse
        v-if="
          [SOURCES.UNIT, SOURCES.SUBSYSTEM_TREE].includes(source) &&
          unit.emergency_contacts &&
          unit.emergency_contacts.length
        "
        :title="$t('grid.slots.info.emergency_contacts')"
        icon="PhoneIcon"
        :unique-id="uniqueId"
        section-key="EmergencyContacts"
        :default-open="false"
        capturable
        ref="refEmergencyContactsCollapse"
      >
        <emergency-contacts :emergency-contacts="unit.emergency_contacts" />
      </section-collapse>

      <section-collapse
        v-if="
          [SOURCES.UNIT, SOURCES.SUBSYSTEM_TREE].includes(source) &&
          riskEvaluationData.snapshot &&
          riskEvaluationData.answersRaw
        "
        :title="$t('grid.slots.info.risk_evaluation')"
        icon="AlertTriangleIcon"
        :unique-id="uniqueId"
        section-key="Riesgo"
        capturable
        ref="refRiskEvaluationst"
      >
        <risk-answers-view
          :snapshot="riskEvaluationData.snapshot"
          :answers="riskEvaluationData.answersRaw"
        />
      </section-collapse>

      <!-- Deactivation -->
      <section-collapse
        v-if="data.data.deactivation != null"
        :title="$t('unit.deactivated_data')"
        icon="XCircleIcon"
        stroke="red"
        capturable
        section-key="deactivation"
      >
        <deactivation-data :deactivation_data="data.data.deactivation" />
      </section-collapse>

      <!-- Router default -->
      <section-collapse
        v-if="
          [SOURCES.UNIT, SOURCES.SUBSYSTEM_TREE].includes(source) &&
          unit.router
        "
        :title="`${$tc('router.title', 1)} 1`"
        class="text-capitalize"
        icon="InfoIcon"
        :unique-id="uniqueId"
        section-key="RouterInformation"
        :default-open="false"
        capturable
        ref="refRouterInformation"
      >
        <router-information :data="unit.router" />
      </section-collapse>

      <!-- Additional Routers -->
      <section-collapse
        v-if="
          [SOURCES.UNIT, SOURCES.SUBSYSTEM_TREE].includes(source) &&
          unit.router
        "
        v-for="(router, index) in unit.additional_routers"
        :key="index"
        :title="`${$tc('router.title', 1)} ${index + 2}`"
        class="text-capitalize"
        icon="InfoIcon"
        :unique-id="`${uniqueId}-${index}`"
        :section-key="`RouterInformation-${index}`"
        :default-open="false"
        capturable
        :ref="`refRouterInformation`"
      >
        <router-information :data="router" />
      </section-collapse>

      <!-- DVR's -->
      <section-collapse
        v-if="
          [SOURCES.UNIT, SOURCES.SUBSYSTEM_TREE].includes(source) &&
          hanwhaDvrs &&
          hanwhaDvrs.length
        "
        :title="$tc('hanwha_dvr.title', 2)"
        class="text-capitalize"
        icon="CameraIcon"
        :unique-id="uniqueId"
        section-key="HanwhaDvrs"
        :default-open="false"
        capturable
        ref="refHanwhaDvrs"
      >
        <hanwha-dvr-information v-for="hanwhaDvr in hanwhaDvrs" :data="hanwhaDvr" :key="hanwhaDvr.mac_address"/>
      </section-collapse>

      <!-- DMP Panels -->
      <section-collapse
        v-if="
          [SOURCES.UNIT, SOURCES.SUBSYSTEM_TREE].includes(source) &&
          dmpPanels &&
          dmpPanels.length
        "
        :title="$t('dmp.panel')"
        class="text-capitalize"
        icon="CameraIcon"
        :unique-id="uniqueId"
        section-key="DmpPanels"
        :default-open="false"
        capturable
        ref="refDmpPanels"
      >
        <dmp-panel-information v-for="dmpPanel in dmpPanels" :data="dmpPanel" :key="dmpPanel.panel_serial_num"/>
      </section-collapse>

      <!-- External Personnel Access Control -->
      <section-collapse
        v-if="
          [SOURCES.UNIT, SOURCES.SUBSYSTEM_TREE].includes(source) &&
          externalPersonnelAccessControl &&
          externalPersonnelAccessControl.length
        "
        :title="$t('operational_security.operational_processes.maintenance')"
        class="text-capitalize"
        icon="ToolIcon"
        :unique-id="uniqueId"
        section-key="DmpPanels2"
        :default-open="false"
        capturable
        ref="refDmpPanels"
      >
        <external-personnel-access-control-information v-for="item in externalPersonnelAccessControl" :key="item.folio" :data="item" />
      </section-collapse>

      <!-- CCB Project Implementation -->
      <section-collapse
        v-if="
          [SOURCES.UNIT, SOURCES.SUBSYSTEM_TREE].includes(source) &&
          ccbProjectImplementation &&
          ccbProjectImplementation.form &&
          ccbProjectImplementation.form.length
        "
        :title="$t('operational_security.operational_processes.project_implementation')"
        icon="BriefcaseIcon"
        :unique-id="uniqueId"
        section-key="CcbProjectImplementation"
        :default-open="false"
        capturable
        ref="refCcbProjectImplementation"
      >
        <ccb-project-implementation-information :data="ccbProjectImplementation" />
      </section-collapse>

      <!-- ===== TABS ===== -->
      <b-tabs
        v-if="source !== 'camera' && source !== 'subsystem_tree' && source !== 'unit'"
        v-model="activeTab"
        pills
        nav-wrapper-class="my-1"
        align="center"
      >
        <!-- Tab: Detalles / Acciones -->
        <b-tab class="tab-detalles">
          <template #title>
            <b-icon icon="exclamation-circle" font-scale="0.9"></b-icon>
            <span style="font-size: 12.4px">{{ $t("alarm.timeline.information") }}</span>
          </template>

          <!-- ===== Contenido para ALARM (YA SIN Resumen/Detalles) ===== -->
          <div v-if="isAlarmSource" class="info-slot-alarm w-100">
            <!-- Información Adicional -->
            <section-collapse
              v-if="data.data.unit.additional_info"
              :title="$t('unit.additional_info')"
              icon="InfoIcon"
              :unique-id="uniqueId"
              section-key="AdditionalInfo"
              :default-open="false"
            >
              <additional-information
                :additional-information="data.data.unit.additional_info"
              />
            </section-collapse>

            <!-- Contactos de emergencia -->
            <section-collapse
              v-if="
                data.data.unit.emergency_contact &&
                data.data.unit.emergency_contact.length > 0
              "
              :unique-id="uniqueId"
              section-key="Contactos"
              :title="$t('grid.slots.info.emergency_contacts')"
              icon="PhoneIcon"
            >
              <emergency-contacts
                :emergency-contacts="data.data.unit.emergency_contact"
              />
            </section-collapse>

            <!-- Seguimiento -->
            <section-collapse
              :unique-id="uniqueId"
              section-key="Seguimiento"
              :title="$t('grid.slots.info.tracing')"
              icon="FileTextIcon"
            >
              <div class="table-responsive">
                <table class="table table-borderless mb-0">
                  <tr v-if="data.data.protocol">
                    <th>{{ $t("grid.slots.info.protocol") }}</th>
                    <td class="fw-bold">{{ data.data.protocol }}</td>
                  </tr>
                  <tr v-if="data.data.attending_comments">
                    <th>{{ $t("grid.slots.info.attending_comments") }}</th>
                    <td class="fw-bold">{{ data.data.attending_comments }}</td>
                  </tr>
                  <tr v-if="data.data.solved_comments">
                    <th>{{ $t("grid.slots.info.solved_comments") }}</th>
                    <td class="fw-bold">{{ data.data.solved_comments }}</td>
                  </tr>
                  <tr v-if="data.data.cancel_comments">
                    <th>{{ $t("grid.slots.info.cancel_comments") }}</th>
                    <td class="fw-bold">{{ data.data.cancel_comments }}</td>
                  </tr>
                  <tr v-if="data.data.alarm_log && data.data.alarm_log.length > 0">
                    <th>{{ $t("grid.slots.info.status") }}</th>
                    <td>
                      <div class="status-log">
                        <div
                          v-for="(log, index) in data.data.alarm_log"
                          :key="index"
                          class="status-item"
                          :class="{
                            'is-attending': log.attending,
                            'is-canceled': log.canceled,
                            'is-attended': log.attended,
                          }"
                        >
                          <div class="status-line">
                            <span class="status-date">{{
                              dateParser.parseDate(log.created)
                            }}</span>
                            <span class="status-sep">•</span>
                            <span class="status-action" v-if="log.attending"
                              >Enviado a atención</span
                            >
                            <span class="status-action" v-else-if="log.canceled"
                              >Cancelado</span
                            >
                            <span class="status-action" v-else-if="log.attended"
                              >Atendido</span
                            >
                            <span class="status-action" v-else-if="log.waiting"
                              >En espera</span
                            >
                          </div>
                          <div class="mt-1 ml-1" v-if="log.user">
                            <span class="status-by">Por: </span>
                            <span class="status-user">{{
                              log.user ? log.user.email : ""
                            }}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </table>
              </div>
            </section-collapse>

            <!-- Evaluación de riesgo -->
            <section-collapse
              v-if="riskEvaluation"
              :title="$t('grid.slots.info.risk_evaluation')"
              icon="AlertTriangleIcon"
              :unique-id="uniqueId"
              section-key="Riesgo"
            >
              <div class="mb-3">
                <p class="h6 mb-2">
                  {{ $t("grid.slots.info.risk_percentage") }}:
                  <span class="text-danger fw-bold">
                    {{ parseInt(riskEvaluation.riskPercentage).toFixed(0) }}%
                  </span>
                </p>

                <risk-form-answers
                  :form-data="riskEvaluation.form"
                  :template-data="riskEvaluation.templateFields"
                  :show-alarms-only="riskEvaluation.alarmsOnly"
                />
              </div>
            </section-collapse>

            <!-- CAD -->
            <section-collapse
              v-if="data.data.cad_number"
              :title="$t('grid.slots.info.cad_data')"
              icon="RadioIcon"
              :unique-id="uniqueId"
              section-key="CAD"
            >
              <div class="table-responsive">
                <table class="table table-borderless mb-0">
                  <tr>
                    <th>{{ $t("grid.slots.info.cad_folio") }}</th>
                    <td class="fw-bold">{{ data.data.cad_number || "-" }}</td>
                  </tr>
                  <tr>
                    <th>{{ $t("grid.slots.info.status") }}</th>
                    <td class="fw-bold">{{ data.data.status_cad || "-" }}</td>
                  </tr>
                  <tr>
                    <th>{{ $t("grid.slots.info.data") }}</th>
                    <td class="fw-bold">{{ data.data.request || "-" }}</td>
                  </tr>
                </table>
              </div>
            </section-collapse>

            <!-- Instituciones -->
            <section-collapse
              v-if="
                data.data.tenant &&
                data.data.tenant.institutions &&
                data.data.tenant.institutions.length > 0
              "
              :title="$t('grid.slots.info.institutions')"
              icon="ShieldIcon"
              :unique-id="uniqueId"
              section-key="Institutions"
            >
              <institutions :alarm="data.data" :tenant="data.data.tenant" />
            </section-collapse>

            <!-- Grupos de Alarma -->
            <section-collapse
              v-if="data.data.alarm_groups && data.data.alarm_groups.length > 0"
              :title="$t('grid.slots.info.alarm_groups')"
              icon="UsersIcon"
              :unique-id="uniqueId"
              section-key="AlarmGroups"
            >
              <alarm-groups :alarm="data.data" />
            </section-collapse>

            <!-- SD -->
            <section-collapse
              v-show="hasSDData"
              :title="$t('grid.slots.info.smart_detection')"
              icon="ActivityIcon"
              :unique-id="uniqueId"
              section-key="SD"
            >
              <sd-data
                :key="`sd-${data.data.id}`"
                :sd-id="data.data.smart_detection_id"
                :host="data.data.host"
                @has-sd-data="(val) => (hasSDData = val)"
              />
            </section-collapse>

            <!-- FR -->
            <section-collapse
              v-show="hasFRData"
              :title="$t('grid.slots.info.fr.title')"
              icon="UserCheckIcon"
              :unique-id="uniqueId"
              section-key="FR"
            >
              <fr-matches
                :key="`fr-${data.data.id}`"
                :fr-data="data.data.facial_recognition"
                @has-fr-data="(val) => (hasFRData = val)"
              />
            </section-collapse>

            <!-- LPR -->
            <section-collapse
              v-show="hasLPRData"
              :title="$t('grid.slots.info.detection_events.lpr.title')"
              icon="TruckIcon"
              :unique-id="uniqueId"
              section-key="LPR"
            >
              <lpr-matches
                :key="`lpr-${data.data.id}`"
                :lpr-data="data.data"
                @has-lpr-data="(val) => (hasLPRData = val)"
              />
            </section-collapse>

            <!-- AR -->
            <section-collapse
              v-show="hasARData"
              :title="$t('grid.slots.info.analytic_record_title')"
              icon="TargetIcon"
              :unique-id="uniqueId"
              section-key="AR"
            >
              <ar-matches
                :key="`analytic-${data.data.id}-${dataVersion}`"
                :data="data.data.analytic_record"
                :totalMatches="data.data.analytic_record_count"
                @has-ar-data="(val) => (hasARData = val)"
              />
            </section-collapse>

            <!-- SIP -->
            <section-collapse
              v-show="hasSIPData"
              :title="$t('grid.slots.info.sip_calls')"
              icon="PhoneCallIcon"
              :unique-id="uniqueId"
              section-key="SIP"
            >
              <sip-calls
                :key="`sip-${data.data.id}-${dataVersion}`"
                :data="data"
                @has-sip-data="(val) => (hasSIPData = val)"
              />
            </section-collapse>

            <!-- PBX -->
            <section-collapse
              v-show="hasPBXData"
              :title="$t('grid.slots.info.pbx_calls')"
              icon="PhoneForwardedIcon"
              :unique-id="uniqueId"
              section-key="PBX"
            >
              <pbx-calls
                :key="`pbx-${data.data.id}-${dataVersion}`"
                :data="data"
                @has-pbx-data="(val) => (hasPBXData = val)"
              />
            </section-collapse>

            <!-- MBX -->
            <section-collapse
              v-show="hasMBXData"
              :title="$t('grid.slots.info.mobotix')"
              icon="CameraIcon"
              :unique-id="uniqueId"
              section-key="MBX"
            >
              <mbx-data
                :key="`mbx-${data.data.id}-${dataVersion}`"
                :id-alarm="data.data.id"
                @has-mbx-data="(val) => (hasMBXData = val)"
              />
            </section-collapse>

            <!-- Alarm Bridge -->
            <section-collapse
              v-show="hasAlarmBridgeData"
              :title="$t('grid.slots.info.alarm_bridge_data')"
              icon="ZapIcon"
              :unique-id="uniqueId"
              section-key="Bridge"
            >
              <alarm-bridge-event-data
                :data="data.data"
                @has-alarm-bridge-data="(val) => (hasAlarmBridgeData = val)"
              />
            </section-collapse>

            <!-- Default Detection Event -->
            <section-collapse
              v-if="
                data.data.detection_event_data != null &&
                !['LPR', 'MBX', 'ME'].includes(data.data.alarm_acronym) &&
                data.data.detection_event_data.provider != 'luna' &&
                data.data.detection_event_data.provider != 'ntechlab' &&
                (data.data.analytic_record ? false : true)
              "
              :title="$t('grid.slots.info.default_detection')"
              icon="EyeIcon"
              :unique-id="uniqueId"
              section-key="DetDefault"
            >
              <detection-event-data-default
                :key="`detectionEvent-data-${data.data.id}-${dataVersion}`"
                :detEvent="data.data"
                :idAlarm="data.data.id"
              />
            </section-collapse>

            <!-- Action Plan Log -->
            <section-collapse
              v-show="hasActionPlanLog"
              :title="$t('grid.slots.info.action_plan_log')"
              icon="CheckSquareIcon"
              :unique-id="uniqueId"
              section-key="APL"
            >
              <action-plan-log
                :key="`aplan-${data.data.id}-${dataVersion}`"
                :data="data.data"
                @has-action-plan-log="(val) => (hasActionPlanLog = val)"
              />
            </section-collapse>

            <!-- Incident Log -->
            <section-collapse
              v-if="data.data.incident_log"
              :title="$t('grid.slots.info.incident_log')"
              icon="AlertOctagonIcon"
              :unique-id="uniqueId"
              section-key="Incident"
            >
              <incident-data
                :key="`incident-${data.data.id}-${dataVersion}`"
                :incident="data.data.incident_log"
              />
            </section-collapse>

            <!-- Workflow Log -->
            <section-collapse
              v-show="hasWorkflowLog"
              :title="$t('grid.slots.info.workflow_logs.title')"
              icon="GitBranchIcon"
              :unique-id="uniqueId"
              section-key="WLog"
            >
              <workflow-log
                :key="`wlog-${data.data.id}-${dataVersion}`"
                :data="data.data"
                @has-workflow-log="(val) => (hasWorkflowLog = val)"
              />
            </section-collapse>

            <!-- Comentarios -->
            <section-collapse
              :title="$tc('alarm.comment', 2)"
              icon="MessageSquareIcon"
              :unique-id="uniqueId"
              section-key="Comments"
            >
              <comment-data :idAlarm="data.data.id" />
            </section-collapse>

            <!-- Enviar a Seproban -->
            <section-collapse
              v-show="hasSeprobanData"
              :title="$t('seproban.title')"
              icon="SendIcon"
              :unique-id="uniqueId"
              section-key="SendToSeproban"
            >
              <send-to-seproban
                v-if="$can('send_to_seproaban', 'Alarm')"
                :alarm-id="data.data.id"
                usage-type="slot"
                @has-seproban-data="(val) => (hasSeprobanData = val)"
              />
            </section-collapse>
          </div>

          <!-- ===== Otros SOURCES ===== -->
          <div v-else>
            <!-- Swift Sensor -->
            <section-collapse
              v-if="data.data.swift_sensor"
              :title="$t('grid.slots.info.swift_sensor')"
              icon="ApertureIcon"
              :unique-id="uniqueId"
              section-key="Swift"
            >
              <swift-sensor-data
                :key="`swift-sensor-${data.data.id}-${dataVersion}`"
                :data="data.data.swift_sensor"
              />
            </section-collapse>

            <section-collapse
              v-if="
                source === SOURCES.SUBSYSTEM_TREE_SENSOR &&
                data.data.sensor_type === 'swift_sensor'
              "
              :title="$t('grid.slots.info.swift_sensor_tree')"
              icon="ApertureIcon"
              :unique-id="uniqueId"
              section-key="SwiftTree"
            >
              <swift-sensor-data
                :key="`swift-sensor-${data.data.id}-${dataVersion}`"
                :data="data.data"
              />
            </section-collapse>

            <!-- Actuator -->
            <section-collapse
              v-if="[SOURCES.ACTUATOR].includes(source)"
              :title="$t('grid.slots.info.actuator')"
              icon="ToggleRightIcon"
              :unique-id="uniqueId"
              section-key="Actuator"
            >
              <actuator-data
                :key="`actuator-${data.data.id}-${dataVersion}`"
                :data="data.data.data"
              />
            </section-collapse>

            <!-- Generic Device -->
            <section-collapse
              v-if="[SOURCES.GENERIC_DEVICE].includes(source)"
              :title="$t('grid.slots.info.generic_device')"
              icon="CpuIcon"
              :unique-id="uniqueId"
              section-key="Generic"
            >
              <generic-device-data
                :key="`generic-device-${data.data.id}-${dataVersion}`"
                :data="data.data.data"
              />
            </section-collapse>
          </div>
        </b-tab>

        <!-- Tab: Timeline -->
        <b-tab>
          <template #title>
            <b-icon icon="clock" font-scale="0.9"></b-icon>
            <span style="font-size: 12.4px">Timeline</span>
          </template>
          <timeline
            :id-alarm="idAlarm"
            :alarm-data="data.data"
            :data-version="dataVersion"
          />
        </b-tab>
      </b-tabs>
    </div>
  </div>
</template>

<script>
import SDData from "./SDData.vue";
import FRMatches from "./FRMatches/Index.vue";
import ARMatches from "./ARMatches/Index.vue";
import LPRMatches from "./LPRMatches/Index.vue";
import UnitData from "./UnitData.vue";
import CameraData from "./CameraData.vue";
import ARData from "./ARData.vue";
import DetectionEventDataDefault from "./DetectionEvent/index.vue";
import { SLOT_TYPES, SOURCES } from "@/config/layoutConfig";
import store from "@/store";
import WorkflowLog from "./WorkflowLog.vue";
import ActionPlanLog from "./ActionPlanLog.vue";
import SipCalls from "./SipCalls/Index.vue";
import PbxCalls from "./PbxCalls/Index.vue";
import IncidentData from "./IncidentData.vue";
import ActuatorData from "./ActuatorData";
import GenericDeviceData from "./GenericDeviceData.vue";
import SwiftSensorData from "./SwiftSensorData";
import DeactivationData from "./DeactivationData.vue";
import MbxData from "./MbxData";
// import LOITData from "./LOITData.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import axiosIns from "@axios";
import LayoutApi from "@/libs/LayoutApi";
import dateParser from "@/libs/date-parser";
import AlarmBridgeEventData from "./AlarmBridgeEventData";
import html2pdf from "html2pdf.js";
import Institutions from "./Institutions.vue";
import HanwhaDvrInformation from "./HanwhaDvrInformation.vue";
import RouterInformation from "./RouterInformation.vue";
import DmpPanelInformation from "./DmpPanelInformation.vue";

import CommentData from "./CommentData/Index.vue";
import axios from "@/libs/axios";
import RiskFormAnswers from "@/components/BuildRiskForm/RiskFormAnswers.vue";
import axios2 from "axios";
import AlarmGroups from "./AlarmGroups.vue";
import SectionCollapse from "./SectionCollapse.vue";
import SendToSeproban from "@/views/components/GridView/widgets/SeprobanModal/SendToSeproban.vue";
import Timeline from "./Timeline/Index.vue";
import EmergencyContacts from "./EmergencyContacts.vue";
import AdditionalInformation from "./AdditionalInformation.vue";
import searchMatchesMixin from "./Mixins/searchMatchesMixin";
import FacetedSearch from "@/components/FacetedSearch.vue";
import {
  UNIT_SEARCH_OPTIONS,
  UNIT_SEARCH_DEFAULT_FIELD,
  fetchFirstUnitMatch,
} from "@/utils/unitSearch";
import debounce from "lodash/debounce";
import RiskAnswersView from "@/components/BuildRiskForm/RiskAnswersView.vue";
import ExternalPersonnelAccessControlInformation from "./ExternalPersonnelAccessControlInformation.vue";
import CcbProjectImplementationInformation from "./CcbProjectImplementationInformation.vue";

const layoutApi = new LayoutApi(store);

export default {
  name: "InfoSlot",
  mixins: [searchMatchesMixin],
  components: {
    SectionCollapse,
    RiskFormAnswers,
    AlarmBridgeEventData,
    "sd-data": SDData,
    "fr-matches": FRMatches,
    "ar-matches": ARMatches,
    "lpr-matches": LPRMatches,
    "ar-data": ARData,
    "mbx-data": MbxData,
    "detection-event-data-default": DetectionEventDataDefault,
    UnitData,
    IncidentData,
    CameraData,
    SipCalls,
    PbxCalls,
    WorkflowLog,
    ActionPlanLog,
    ActuatorData,
    GenericDeviceData,
    SwiftSensorData,
    CommentData,
    Institutions,
    AlarmGroups,
    SendToSeproban,
    Timeline,
    EmergencyContacts,
    AdditionalInformation,
    FacetedSearch,
    RiskAnswersView,
    DeactivationData,
    HanwhaDvrInformation,
    RouterInformation,
    DmpPanelInformation,
    ExternalPersonnelAccessControlInformation,
    CcbProjectImplementationInformation
  },
  props: {
    data: { type: Object, required: true },
    tabInfo: Object,
    showMatchesSearch: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      SOURCES,
      idAlarm: null,
      dataVersion: 1,
      dateParser,
      riskEvaluation: null,
      copiedTooltip: this.$t("grid.slots.info.copy_id"),
      activeTab: 0,
      hasAlarmBridgeData: false,
      hasActionPlanLog: false,
      hasWorkflowLog: false,
      hasSDData: false,
      hasFRData: false,
      hasLPRData: false,
      hasARData: false,
      hasSIPData: false,
      hasPBXData: false,
      hasMBXData: false,
      hasCommentsData: false,
      hasSeprobanData: false,
      hasAdditionalInfo: false,
      instanceId: Math.random().toString(36).substr(2, 9), // ID único para esta instancia
      unit: {},
      searchIntoSlotContainerQuery: "",
      originalInfoSlotContainer: null,
      riskEvaluationData: {
        answersRaw: null,
        snapshot: null,
      },
      elapsedAlarmStatusSeconds: 0,
      timerAlarmStatusInterval: null,
      alarmFormTemplates: [],
      hanwhaDvrs: [],
      dmpPanels: [],
      externalPersonnelAccessControl: [],
      ccbProjectImplementation: null,
      unitSearchKey: 0,
      // Local half of the matches-search toggle; the prop is the parent's half
      // (the units view drives it from its own control).
      localMatchesSearch: true
    };
  },
  created() {
    this.ensureAlarmPayloadShape();
    this.idAlarm = this.data.data.id;
    this.fetchAlarmFormTemplates();
  },
  mounted() {
    if ([SOURCES.UNIT, SOURCES.SUBSYSTEM_TREE].includes(this.source)) {
      const mainContainer = this.$refs.refInfoSlot;

      this.setMainContainer(mainContainer);
      this.setRefs(this.$refs);

      this.checkSectionsCollapses = debounce(() => {
        const searchText = this.searchIntoSlotContainerQuery.trim();

        for (const keyRef in this.$refs) {
          if (keyRef !== "refInfoSlot") {
            const refComponent = this.$refs[keyRef];

            // Refs of unmounted conditional sections linger as undefined.
            if (!refComponent || !refComponent.$el) continue;

            this.searchMatches(refComponent.$el, searchText, keyRef);
          }
        }
      }, 500);
    }
    if (this.isAlarmSource) {
      this.fetchAlarmLog();
      this.fetchRiskEvaluation();
    }
    this.setWebsocket();
  },
  beforeDestroy() {
    if (this.timerAlarmStatusInterval) clearInterval(this.timerAlarmStatusInterval);
    window.Echo.leave(`alarm-${this.idAlarm}`);
  },
  watch: {
    // Hiding the input cancels the search: without clearing the query the
    // highlights it painted would stay in the sections with no way to reach
    // them.
    matchesSearchVisible(visible) {
      if (visible || !this.searchIntoSlotContainerQuery) return;

      this.searchIntoSlotContainerQuery = "";
      this.checkMatches();
    },
  },
  computed: {
    userData() {
      return store.state.user.user;
    },
    source() {
      return this.data.data.in_app_source;
    },
    isAlarmSource() {
      return this.source === "alarm" || this.source === "alarm_view";
    },
    alarmStatus() {
      switch (this.data.data.alarm_status) {
        case "waiting":
          return "Incoming";
        case "attending":
          return "Attending";
        case "attended":
          return "Attended";
        case "canceled":
          return "Canceled";
        default:
          return "";
      }
    },
    uniqueId() {
      return `alarm-${this.idAlarm}-${this.instanceId}`;
    },
    computedShowFilterUnits() {
      return this.data?.data.showFilterUnits ?? false;
    },
    unitSearchOptions() {
      return UNIT_SEARCH_OPTIONS;
    },
    unitSearchDefaultField() {
      return UNIT_SEARCH_DEFAULT_FIELD;
    },
    matchesSearchVisible() {
      return this.showMatchesSearch && this.localMatchesSearch;
    },
    showStickySearchBar() {
      if (![SOURCES.UNIT, SOURCES.SUBSYSTEM_TREE].includes(this.source)) {
        return false;
      }

      return this.matchesSearchVisible || this.computedShowFilterUnits;
    },
    elapsedFormatted() {
      if (this.elapsedAlarmStatusSeconds === null) return "--:--:--";
      const h = Math.floor(this.elapsedAlarmStatusSeconds / 3600);
      const m = Math.floor((this.elapsedAlarmStatusSeconds % 3600) / 60);
      const s = this.elapsedAlarmStatusSeconds % 60;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(h)}:${pad(m)}:${pad(s)}`;
    },
  },
  methods: {
    ensureAlarmPayloadShape() {
      if (!this.data || !this.data.data) {
        return;
      }
      const currentData = this.data.data;
      const fallbackUnit = {
        description: "",
        company: "",
        address: "",
        address_1: "",
        address_2: "",
        exterior_number: "",
        suburb: "",
        postal_code: "",
        rems: "",
        sirh: "",
        id_seproban: "",
        municipality: { name: "" },
        country: { name: "" },
        region: { name: "" },
        emergency_contact: [],
        additional_info: [],
      };
      const nextData = {
        ...currentData,
        alarm_type: currentData.alarm_type || { description: "" },
        unit: {
          ...fallbackUnit,
          ...(currentData.unit || {}),
          municipality: {
            ...fallbackUnit.municipality,
            ...(currentData.unit?.municipality || {}),
          },
          country: {
            ...fallbackUnit.country,
            ...(currentData.unit?.country || {}),
          },
          region: {
            ...fallbackUnit.region,
            ...(currentData.unit?.region || {}),
          },
          emergency_contact: Array.isArray(currentData.unit?.emergency_contact)
            ? currentData.unit.emergency_contact
            : fallbackUnit.emergency_contact,
          additional_info: Array.isArray(currentData.unit?.additional_info)
            ? currentData.unit.additional_info
            : fallbackUnit.additional_info,
        },
        sip_call: Array.isArray(currentData.sip_call) ? currentData.sip_call : [],
      };
      this.data.data = nextData;
    },
    async fetchAlarmLog() {
      try {
        const { data } = await axios.get(`/v1/alarmlog/get/${this.data.data.id}`);
        const log = data.data;
        if (log && log.created) {
          const createdAt = new Date(log.created).getTime();
          this.elapsedAlarmStatusSeconds = Math.floor((Date.now() - createdAt) / 1000);
          this.timerAlarmStatusInterval = setInterval(() => {
            this.elapsedAlarmStatusSeconds++;
          }, 1000);
        } else {
          this.elapsedAlarmStatusSeconds = null;
        }
      } catch (e) {
        console.error("Error fetching alarm log:", e);
        this.elapsedAlarmStatusSeconds = null;
      }
    },
    handleCloseUnit() {
      const { showFilterUnits, in_app_source } = this.data.data;

      this.data.data = { showFilterUnits, in_app_source };
      this.ensureAlarmPayloadShape();
      this.resetUnitSections();
      // Remount the searcher so its chips go with the unit they found.
      this.unitSearchKey += 1;
    },
    resetUnitSections() {
      this.unit = {};
      this.hanwhaDvrs = [];
      this.dmpPanels = [];
      this.externalPersonnelAccessControl = [];
      this.ccbProjectImplementation = null;
      this.riskEvaluationData = { answersRaw: null, snapshot: null };
    },
    handleSelectUnit(event) {
      this.data.data = { ...this.data.data, ...event }; //sets new unit to show
      this.ensureAlarmPayloadShape();
    },
    async openFirstUnitMatch(filters) {
      if (!filters || !Object.keys(filters).length) return;

      try {
        const unit = await fetchFirstUnitMatch(filters);

        if (!unit) {
          this.showMessage(this.$t("no_record_found"), "", "warning");
          return;
        }

        // The endpoint names it in the singular; the emergency contacts
        // section reads the plural, like the units view does on row select.
        this.handleSelectUnit({
          ...unit,
          emergency_contacts: unit.emergency_contact,
        });
      } catch (e) {
        console.error(e);
        this.showMessage(this.$t("messages.error.server_error"), "", "danger");
      }
    },
    async copyAlarmId() {
      try {
        await navigator.clipboard.writeText(this.data.data.id.toString());
        this.copiedTooltip = this.$t("grid.slots.info.copied");
        setTimeout(() => {
          this.copiedTooltip = this.$t("grid.slots.info.copy_id");
        }, 2000);
      } catch (err) {
        this.showMessage("Error", "No se pudo copiar el ID al portapapeles", "danger");
      }
    },
    async onClickUpdateAlarmStatus(status) {
      if (status === "send_to_review") {
        await this.takeOrReleaseAlarm("review", this.idAlarm, "IN_REVIEW");
        return;
      }

      const action = (s) =>
        s === "waiting" ? "confirmed" : s === "attending" ? "attended" : "canceled";
      await store.dispatch("grid/setSelectedAlarm", {
        action: action(status),
        alarm: this.data.data,
        slot: { pos: this.data.pos },
      });
      this.$root.$emit("bv::show::modal", "alarm_action_modal");
    },
    async onClickReopenAlarm() {
      const alarmCopy = JSON.parse(JSON.stringify(this.data.data));
      await store.dispatch("grid/setSelectedAlarm", {
        action: "reopen",
        alarm: alarmCopy,
        slot: { pos: this.data.pos },
      });
      this.$root.$emit("bv::show::modal", "grid-alarm-reopen-modal");
    },
    fetchAlarmFormTemplates() {
      axios
        .get("/v1/operational-security-forms/templates", {
          params: {
            display_in_alarm: true,
          },
        })
        .then((response) => {
          this.alarmFormTemplates = response.data?.data || [];
        })
        .catch(() => {
          this.alarmFormTemplates = [];
        });
    },
    navigateToFormTemplate(template) {
      store.dispatch("grid/setSelectedAlarm", {
        action: "form_template",
        alarm: this.data.data,
      });
      this.$router
        .push({
          name: "OperationalSecurityFormAnswers",
          query: {
            startForm: String(template.id),
            requiresPrefill: "true",
            unitId: this.data.data.unit?.id,
          },
        })
        .catch(() => {});
    },
    showMessage(title, msg, variant = "success") {
      this.$toast({
        component: ToastificationContent,
        position: "top-right",
        props: { title, variant, icon: "BellIcon", text: msg, timeout: 5000 },
      });
    },
    takeCall(userData, sessionId, callingPartyNumber, alarm) {
      alarm = alarm.data;
      const deviceInterphone = userData.device_interphone;
      store
        .dispatch("sip/takeCall", {
          sessionId,
          user: deviceInterphone.device_id,
          presavedCaller: callingPartyNumber,
          alarm,
        })
        .then((response) => this.showMessage(response.message, "", "success"))
        .catch((response) => {
          if (response.response?.status == 424) {
            this.showMessage(response.response.data.message, "", "danger");
          }
        });
    },
    setWebsocket() {
      window.Echo.leave(`alarm-${this.idAlarm}`);
      window.Echo.private(`alarm-${this.idAlarm}`).listen(".updated", () =>
        this.updateAlarmData()
      );
      setTimeout(() => {
        if (this.dataVersion === 1) this.updateAlarmData();
      }, 4000);
    },
    async updateAlarmData() {
      if (!this.data.data.refresh_link) return;

      let headers = this.requestConfig();
      let token = localStorage.getItem("accessToken");
      let resp;

      if (this.data.data.host !== window.__env.VUE_APP_BASE_URL) {
        const externalUser = this.$store.state.user.user.external_users.find(
          (eu) => eu.host == this.data.data.host
        );
        if (externalUser) token = externalUser.token;
        headers = { ...headers, Authorization: `Bearer ${token}` };
        resp = await axios2.get(this.data.data.refresh_link, { headers });
      } else {
        resp = await axiosIns.get(this.data.data.refresh_link);
      }

      layoutApi.updateSlotData(this.data.pos, SLOT_TYPES.INFO, resp.data);
      this.ensureAlarmPayloadShape();
      this.dataVersion += 1;
    },
    requestConfig() {
      const token = localStorage.getItem("accessToken");
      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-localization": store.getters["user/getPreferences"].lang,
      };
    },
    changeData(evt) {
      this.data.data = evt;
      this.ensureAlarmPayloadShape();
      this.dataVersion += 1;
    },
    exportToPDF() {
      html2pdf(document.querySelector(".info-slot-container"), {
        margin: 1,
        filename: `alarm-details-${this.idAlarm}.pdf`,
        image: { type: "jpeg", quality: 0.5 },
        html2canvas: { useCORS: true },
      });
    },
    // Alarm sources only: the unit sources read the evaluation off the unit's
    // own last_risk_evaluation in setUnit.
    async fetchRiskEvaluation() {
      const unitId = this.data.data.unit?.id;

      if (!unitId) return;

      try {
        const params = { params: { id_unit: unitId, max: 1 } };
        const response = await axios.get("/v1/risk_evaluations", params);
        const [latest] = response.data.data || [];

        if (latest) {
          await this.fetchFormEvaluation(latest.id);
        }
      } catch (e) {
        console.error(e);
      }
    },
    async fetchHanwhaDvr() {
      try {
        const params = { params: { id_unit: this.unit.id } };
        const response = await axios.get("/v1/hanwha/dvr", params);
        this.hanwhaDvrs = response.data.data || [];
      } catch (e) {
        console.error(e);
      }
    },
    async fetchPanel() {
      try {
        const params = { params: { id_unit: this.unit.id } };
        const response = await axios.get("/v1/dmp/panels", params);
        this.dmpPanels = response.data.data || [];
      } catch (e) {
        console.error(e);
      }
    },
    async fetchExternalPersonnelAccessControl() {
      try {
        const params = { params: { id_unit: this.unit.id, statuses: ['in_progress', 'partial'] } };
        const response = await axios.get("/v1/external-personnel-access-control", params);
        this.externalPersonnelAccessControl = response.data.data || [];
      } catch (e) {
        console.error(e);
      }
    },
    async fetchCcbProjectImplementation() {
      try {
        const params = { params: { id_facility: this.unit.id } };
        const response = await axios.get(
          "/v1/operational-security-forms/ccb-implementacion-proyectos",
          params
        );
        this.ccbProjectImplementation = response.data.data || null;
      } catch (e) {
        console.error(e);
      }
    },
    async fetchFormEvaluation(riskEvaluationId) {
      const response = await axios.get(
        `/v1/risk_form_answers/get-form-by-evaluation/${riskEvaluationId}`
      );
      const formData = response.data.data;

      this.setRiskEvaluation(formData);
    },
    setUnit(unit) {
      // axios drops an undefined param, so these reads would come back
      // unfiltered and paint another unit's recorders and panels.
      if (!unit || !unit.id) {
        this.resetUnitSections();
        return;
      }

      const { last_risk_evaluation } = unit;

      this.unit = unit;

      this.fetchHanwhaDvr();
      this.fetchPanel();
      this.fetchExternalPersonnelAccessControl();
      this.fetchCcbProjectImplementation();

      if (last_risk_evaluation) {
        const { form_answers = [] } = last_risk_evaluation;

        if (form_answers.length) {
          const [formAnswerData] = form_answers;
          const formData = this.getFormData(formAnswerData);

          this.riskEvaluationData.answersRaw = formData.data || null;
          this.riskEvaluationData.snapshot = formData.template_snapshot || null;
        }
      }
    },
    getFormData(dataForm) {
      let template = {};

      if (dataForm.template) {
        template = {
          ...dataForm.template,
          data: dataForm.template.data.filter((d) => d.options.alarms),
        };
      }

      const formData = {
        ...dataForm,
        template,
      };

      return formData;
    },
    checkMatches() {
      this.initSearchMatchesData();
      this.checkSectionsCollapses();
    },
    setRiskEvaluation(formData) {
      const { template_snapshot: snapshot, template } = formData;
      const templateFields =
        Array.isArray(snapshot) && snapshot.length ? snapshot : template?.data || [];

      this.riskEvaluation = {
        form: formData.data,
        templateFields,
        alarmsOnly: templateFields.some((field) => field?.options?.alarms === true),
        riskPercentage: formData.risk_percentage,
      };
    },
    async takeOrReleaseAlarm(action, idAlarm, status = "") {
      const payload = {
        id_alarm: idAlarm,
        action,
        status,
      };
      await store.dispatch("taken_alarms/takeAlarm", payload);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../../../assets/scss/variables/variables";

// Search header of the F3 slot: searcher, matches toggle and close on one
// row, mirroring the units view in slot mode.
.unit-filter-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  .dark-layout & {
    border-bottom-color: rgba(255, 255, 255, 0.06);
  }
}

.unit-filter-header__search {
  flex: 1 1 0;
  min-width: 0;
}

.unit-filter-header__actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: auto;
}

.info-slot-container {
  background-color: var(--main-light) !important;
  padding-block: 0.5rem;

  .tabs .tab-content {
    height: auto !important;
  }
}

:deep(.info-slot) {
  &:fullscreen,
  &:-webkit-full-screen,
  &:-moz-full-screen,
  &:-ms-fullscreen {
    .info-slot-container {
      max-height: none !important;
      height: auto !important;
      overflow-y: visible !important;
    }

    .tabs .tab-content {
      max-height: none !important;
      overflow-y: visible !important;
    }

    .tab-pane {
      max-height: none !important;
      overflow-y: visible !important;
    }
  }
}

.text-header {
  margin-bottom: 0.5rem;
}

.dark-layout {
  .info-slot-container {
    background-color: var(--third-dark) !important;
    color: var(--text-light) !important;
  }
}

.grid-tabs .tabs {
  background-color: transparent !important;
}

.grid-tabs .tabs .tab-content .tab-pane {
  overflow: unset;
}

.alarm-export-button {
  position: absolute;
  right: 1rem;
  top: 2rem;
  transform: translateY(-50%);
  z-index: 1;
}

.clickable {
  cursor: pointer;
  &:hover {
    color: #2d7ff9 !important;
  }
}

.copy-icon {
  color: #0d6efd;
}

.status-log .status-item {
  padding: 0.25rem 0;
  background-color: var(--main-light);
  .dark-layout & {
    background-color: var(--second-dark);
  }
}
.status-line {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding-inline: 1rem;
}
.status-date {
  font-weight: 600;
  .dark-layout & {
    color: $theme-dark-label-color !important;
  }
}
.status-sep {
  opacity: 0.5;
}

// Opaque on purpose: the sections scroll under this bar.
.sticky-top-input {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: var(--main-light);

  .dark-layout & {
    background-color: var(--third-dark);
  }
}
</style>
